import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { commandApi } from 'app/apis/commandApi';
import { RootState } from 'app/store';
import { selectSite } from 'features/topbar/selectedSiteSlice';
import { PURGE } from 'redux-persist';

export interface BaseSelectedRow {
  rowId: string;
  rowData: any;
}

export interface RowSelectionState {
  componentId?: string;
  rows: BaseSelectedRow[];
}

const initialState = {
  rowSelections: [] as RowSelectionState[]
};

const rowSelectionSlice = createSlice({
  name: 'rowSelection',
  initialState,
  reducers: {
    selectRow: (
      state,
      {
        payload: { componentId, selectedRow }
      }: PayloadAction<{
        componentId: string;
        selectedRow: BaseSelectedRow;
      }>
    ) => {
      const rowSelectionState = state.rowSelections.find(
        (rowSelection) => rowSelection.componentId === componentId
      );

      if (!rowSelectionState) {
        const newRowSelectionState: RowSelectionState = {
          componentId,
          rows: [selectedRow]
        };

        state.rowSelections = [...state.rowSelections, newRowSelectionState];
      } else {
        const rowShouldBeAdded = !rowSelectionState.rows.some(
          (row) => row.rowId === selectedRow.rowId
        );

        rowSelectionState.rows = rowShouldBeAdded
          ? [...rowSelectionState.rows, selectedRow]
          : rowSelectionState.rows.filter(
              (row) => row.rowId !== selectedRow.rowId
            );
      }
    },
    selectRows: (
      state,
      {
        payload: { componentId, selectedRows }
      }: PayloadAction<{
        componentId: string;
        selectedRows: BaseSelectedRow[];
      }>
    ) => {
      const rowSelectionState = state.rowSelections.find(
        (rowSelection) => rowSelection.componentId === componentId
      );

      if (!rowSelectionState) {
        const newRowSelectionState: RowSelectionState = {
          componentId,
          rows: selectedRows
        };

        state.rowSelections = [...state.rowSelections, newRowSelectionState];
      } else {
        const shouldRemoveRows = selectedRows.every((row) =>
          rowSelectionState.rows.some((r) => r.rowId === row.rowId)
        );

        if (shouldRemoveRows) {
          rowSelectionState.rows = rowSelectionState.rows.filter(
            (row) => !selectedRows.some((r) => r.rowId === row.rowId)
          );
        } else {
          const rowsToAdd = selectedRows.filter(
            (row) => !rowSelectionState.rows.some((r) => r.rowId === row.rowId)
          );

          rowSelectionState.rows = [...rowSelectionState.rows, ...rowsToAdd];
        }
      }
    },
    clearRowSelections: (state) => {
      state.rowSelections = [];
    }
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState);
    builder.addCase(selectSite, (state, action) => {
      state.rowSelections = [];
    });
    builder
      .addMatcher(
        commandApi.endpoints.deleteMultipleRevenues.matchFulfilled,
        (
          state,
          {
            meta: {
              arg: {
                originalArgs: { componentId, revenue }
              }
            }
          }
        ) => {
          const rowSelection = state.rowSelections.find(
            (rowSelection) => rowSelection.componentId === componentId
          );
          if (!rowSelection) return;

          const filteredRows = rowSelection.rows.filter(
            (row) => !revenue.map((rev) => rev.revenueId).includes(row.rowId)
          );

          rowSelection.rows = filteredRows;
        }
      )
      .addMatcher(
        commandApi.endpoints.deleteMultipleOpportunities.matchFulfilled,
        (
          state,
          {
            meta: {
              arg: {
                originalArgs: { componentId, opportunities }
              }
            }
          }
        ) => {
          const rowSelection = state.rowSelections.find(
            (rowSelection) => rowSelection.componentId === componentId
          );
          if (!rowSelection) return;

          rowSelection.rows = [];
        }
      );
  }
});

const selectSelf = (state: RootState) => state.rowSelection;
const selectRowSelectionByComponentId = (
  state: RootState,
  componentId: string
) => componentId;

export const selectRowSelectionsByComponentId = createSelector(
  selectSelf,
  selectRowSelectionByComponentId,
  (rowSelection, componentId) => {
    const rowSelectionState = rowSelection.rowSelections.find(
      (rowSelection) => rowSelection.componentId === componentId
    );

    const selectedRows: any = { rows: [] };

    if (!rowSelectionState?.rows) {
      return selectedRows;
    }

    rowSelectionState.rows.forEach(
      (row) => (selectedRows.rows[row.rowId] = true)
    );

    return selectedRows;
  }
);
export const selectRowsByComponentId = createSelector(
  selectSelf,
  selectRowSelectionByComponentId,
  (rowSelection, componentId) => {
    const rowSelectionState = rowSelection.rowSelections.find(
      (rowSelection) => rowSelection.componentId === componentId
    );

    if (!rowSelectionState?.rows) {
      return [];
    }

    return rowSelectionState.rows;
  }
);

export const selectRowByIdAndComponentId = createSelector(
  selectSelf,
  selectRowSelectionByComponentId,
  (rowSelection, componentId) => (rowId: string) => {
    const rowSelectionState = rowSelection.rowSelections.find(
      (rowSelection) => rowSelection.componentId === componentId
    );

    if (!rowSelectionState?.rows) {
      return null;
    }

    return rowSelectionState.rows.find((row) => row.rowId === rowId);
  }
);

const { actions, reducer } = rowSelectionSlice;

export const { selectRow, selectRows } = actions;

export default reducer;
